import React, { useState } from 'react';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { SALESFORCE_FIELD_MAX_LENGTH, exceedsFieldLength, fieldLengthProps } from '../../constants';
import type { CreatedAsset, RoomAssetLink } from '../packageOptions/types';

import ApplyInclusionDialog from './ApplyInclusionDialog';
import AssetLinkField from './AssetLinkField';
import {
  INCLUSION_APPLIES_OPTIONS,
  INCLUSION_FIELD_LABELS,
  INCLUSION_TYPE_OPTIONS,
  INCLUSION_VALID_FOR_OPTIONS,
} from './fields';
import { describeApplyTo, describeInclusionValue, getAvailableOccupancies, isApplyToValid } from './inclusionsModel';
import type { PackageRoomRow } from './inclusionsModel';
import type { InclusionAssetLink, InclusionDraft, InclusionValidFor } from './types';

interface Props {
  assets: Array<RoomAssetLink>;
  shownAssets: Array<RoomAssetLink>;
  currency: string;
  draft: InclusionDraft;
  onCreateAsset: (name: string) => CreatedAsset;
  onChange: (changes: Partial<InclusionDraft>) => void;
  onRemove: () => void;
  nights: Array<number>;
  rooms: Array<PackageRoomRow>;
}

// Every field writes straight back to the draft on change; "Applies to" is the one field that
// opens a dialog.
export default function InclusionEditCard({
  assets,
  shownAssets,
  currency,
  draft,
  onCreateAsset,
  onChange,
  onRemove,
  nights,
  rooms,
}: Props) {
  const [applyToDialogOpen, setApplyToDialogOpen] = useState(false);
  const matchesAnyPackage = isApplyToValid(rooms, nights, draft.applyTo);

  // Picking an asset names the inclusion after it, unless the CM already typed their own name.
  const changeAssetLink = (assetLink: InclusionAssetLink | undefined) => {
    const nameFollowsAsset = !draft.name.trim() || draft.name === draft.assetLink?.assetName;
    onChange(assetLink && nameFollowsAsset ? { assetLink, name: assetLink.assetName } : { assetLink });
  };

  // Creating an asset always renames the inclusion, even over a preset or typed name.
  const createAsset = (name: string) => {
    const asset = onCreateAsset(name);
    onChange({ name: asset.name });
    return asset;
  };

  const toggleValidFor = (option: InclusionValidFor) => {
    onChange({
      validFor: draft.validFor.includes(option)
        ? draft.validFor.filter((entry) => entry !== option)
        : [...draft.validFor, option],
    });
  };

  return (
    <Card variant="outlined">
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="flex-start">
            <TextField
              label={INCLUSION_FIELD_LABELS.name}
              value={draft.name}
              onChange={(event) => onChange({ name: event.target.value })}
              {...fieldLengthProps(draft.name, SALESFORCE_FIELD_MAX_LENGTH.inclusionName)}
              error={!draft.name.trim() || exceedsFieldLength(draft.name, SALESFORCE_FIELD_MAX_LENGTH.inclusionName)}
              size="small"
              required
              sx={{ minWidth: 180, flex: 2 }}
            />
            <AssetLinkField
              assets={assets}
              shownAssets={shownAssets}
              currency={currency}
              onChange={changeAssetLink}
              onCreateAsset={createAsset}
              value={draft.assetLink}
            />
            <TextField
              size="small"
              label="Applies to"
              value={describeApplyTo(draft.applyTo, rooms)}
              error={!matchesAnyPackage}
              helperText={matchesAnyPackage ? undefined : 'Matches no package'}
              onClick={() => setApplyToDialogOpen(true)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setApplyToDialogOpen(true);
                }
              }}
              InputProps={{ readOnly: true }}
              sx={{ minWidth: 160, flex: 1, cursor: 'pointer' }}
            />
            <TextField
              label={INCLUSION_FIELD_LABELS.rrp}
              type="number"
              value={draft.rrp ?? ''}
              onChange={(event) =>
                onChange({ rrp: event.target.value === '' ? undefined : Number(event.target.value) })
              }
              error={draft.rrp !== undefined && draft.rrp < 0}
              inputProps={{ min: 0 }}
              size="small"
              InputProps={{ startAdornment: <InputAdornment position="start">{currency}</InputAdornment> }}
              sx={{ minWidth: 160, flex: 1 }}
            />
            <TextField
              label={INCLUSION_FIELD_LABELS.prv}
              type="number"
              value={draft.prv ?? ''}
              onChange={(event) =>
                onChange({ prv: event.target.value === '' ? undefined : Number(event.target.value) })
              }
              size="small"
              helperText="Not sent to Salesforce"
              InputProps={{ startAdornment: <InputAdornment position="start">{currency}</InputAdornment> }}
              sx={{ minWidth: 160, flex: 1 }}
            />
            <TextField
              select
              label={INCLUSION_FIELD_LABELS.type}
              value={draft.type ?? ''}
              onChange={(event) => onChange({ type: event.target.value || undefined })}
              error={!draft.type}
              required
              size="small"
              sx={{ minWidth: 130, flex: 1 }}
            >
              {INCLUSION_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={INCLUSION_FIELD_LABELS.applies}
              value={draft.applies ?? ''}
              onChange={(event) =>
                onChange({ applies: (event.target.value || undefined) as InclusionDraft['applies'] })
              }
              error={!draft.applies}
              required
              size="small"
              sx={{ minWidth: 130, flex: 1 }}
            >
              {INCLUSION_APPLIES_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <IconButton size="small" onClick={onRemove} aria-label="Remove inclusion" sx={{ mt: 0.5 }}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="flex-start">
            <TextField
              label="Fine print"
              value={draft.description ?? ''}
              onChange={(event) => onChange({ description: event.target.value })}
              size="small"
              sx={{ minWidth: 240, flex: 2 }}
            />
            <Stack direction="row" spacing={1} sx={{ pt: 0.5 }}>
              {INCLUSION_VALID_FOR_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      size="small"
                      checked={draft.validFor.includes(option)}
                      onChange={() => toggleValidFor(option)}
                    />
                  }
                  label={option}
                />
              ))}
            </Stack>
          </Stack>

          <Typography variant="caption" color="text.secondary" fontStyle={draft.prv ? undefined : 'italic'}>
            {describeInclusionValue({
              prv: draft.prv,
              type: draft.type,
              applies: draft.applies,
              leadNights: nights[0],
            })}
          </Typography>
        </Stack>
      </CardContent>

      <ApplyInclusionDialog
        open={applyToDialogOpen}
        value={draft.applyTo}
        rooms={rooms}
        occupancies={getAvailableOccupancies(rooms)}
        nights={nights}
        onClose={() => setApplyToDialogOpen(false)}
        onApply={(applyTo) => onChange({ applyTo })}
      />
    </Card>
  );
}
